<script setup lang="ts">
import { ref, watch, nextTick, computed } from 'vue'
import { Promotion, Microphone } from '@element-plus/icons-vue'
import { useUserStore } from '@/stores/userStore'
import { useDictation } from '@/composables/useDictation'

// Props
interface Props {
  modelValue: string
  placeholder?: string
  hasAttachments?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Enter message...',
  hasAttachments: false,
})

const userStore = useUserStore()

// Emits
const emit = defineEmits<{
  'update:modelValue': [value: string]
  send: []
}>()

// State
const textareaRef = ref<HTMLTextAreaElement>()
const localValue = ref(props.modelValue)

// Computed
const canSend = computed(() => !!(localValue.value.trim() || props.hasAttachments))

// Watch for prop changes
watch(
  () => props.modelValue,
  (newValue) => {
    localValue.value = newValue
    nextTick(() => adjustTextareaHeight())
  },
)

// Handle input
function handleInput(e: Event) {
  const target = e.target as HTMLTextAreaElement
  localValue.value = target.value
  emit('update:modelValue', target.value)
  adjustTextareaHeight()
}

// Textarea auto-resize
function adjustTextareaHeight() {
  if (!textareaRef.value) return
  textareaRef.value.style.height = '0' // 0にリセットしてscrollHeightにコンテンツの高さを反映させる。縮小処理に必要。
  textareaRef.value.style.height = `${textareaRef.value.scrollHeight}px`
}

// Send message
function handleSend() {
  if (!canSend.value) return
  stopRecording()
  emit('send')
}

// --- voice dictation (Web Speech API) ---
const { isVoiceSupported, isRecording, toggleRecording, stopRecording } = useDictation({
  getBaseText: () => localValue.value,
  onTranscript: (text) => {
    localValue.value = text
    emit('update:modelValue', text)
    nextTick(adjustTextareaHeight)
  },
  lang: () => userStore.get_current_user.preferences.dictationLang,
})

// Expose methods for parent component
defineExpose({
  focus: () => textareaRef.value?.focus(),
  adjustHeight: adjustTextareaHeight,
})
</script>

<template>
  <div class="message-input-container">
    <textarea
      ref="textareaRef"
      :value="localValue"
      class="input-textarea"
      :placeholder="placeholder"
      @input="handleInput"
    />
    <button
      v-if="isVoiceSupported"
      class="mic-btn"
      :class="{ recording: isRecording }"
      :aria-label="isRecording ? 'Stop dictation' : 'Dictate message'"
      @click="toggleRecording"
    >
      <el-icon :size="20">
        <Microphone />
      </el-icon>
    </button>
    <button
      class="send-btn"
      :class="{ active: canSend }"
      :disabled="!canSend"
      aria-label="Send"
      @click="handleSend"
    >
      <el-icon :size="20">
        <Promotion />
      </el-icon>
    </button>
  </div>
</template>

<style scoped>
.message-input-container {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  background: #f5f7fa;
  border-radius: 20px;
  padding: 6px 10px;
  transition: all 0.3s;

  &:focus-within {
    background: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .input-textarea {
    flex: 1;
    min-height: 60px;
    max-height: var(--chat-input-max-height, 120px);
    padding: 8px 6px;
    border: none;
    background: transparent;
    font-size: var(--font-size-body);
    line-height: var(--line-height-normal);
    resize: none;
    outline: none;
    font-family: inherit;
    color: #303133;
    overflow-y: auto;

    &::placeholder {
      color: #c0c4cc;
    }

    /* Scrollbar styling */
    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: #dcdfe6;
      border-radius: 2px;

      &:hover {
        background: #c0c4cc;
      }
    }
  }

  .mic-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: #e0e0e0;
    color: #606266;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    flex-shrink: 0;
    align-self: flex-end;
    margin-bottom: 2px;

    &:hover {
      background: #d0d0d0;
    }

    &:active {
      transform: scale(0.95);
    }

    &.recording {
      background: #f56c6c;
      color: white;
      animation: micPulse 1.2s ease-in-out infinite;
    }
  }

  .send-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: #e0e0e0;
    color: #909399;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    flex-shrink: 0;
    align-self: flex-end;
    margin-bottom: 2px;

    &.active {
      background: #409eff;
      color: white;

      &:hover {
        background: #66b1ff;
      }
    }

    &:active:not(:disabled) {
      transform: scale(0.95);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
}

@keyframes micPulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(245, 108, 108, 0.5);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(245, 108, 108, 0);
  }
}
</style>
